import { useEffect, useRef, useState } from 'react';
import { Clock3 } from 'lucide-react';
import { Input } from '@/components/atoms/input';
import {
  DURATION_HINT,
  DURATION_PLACEHOLDER,
  formatDurationFromMinutes,
  parseDurationToMinutes,
} from '@/lib/time/duration';
import { cn } from '@/lib/utils';

/**
 * The labor-time field.
 *
 * Every place the app asks how long work takes uses this, so the answer means
 * the same thing everywhere: a bare number is HOURS, and "30m", "1h 30m" or
 * "1:30" are read as written. The number spinners it replaces forced the
 * conversion onto the operator, and a mistyped 1.5 is a half hour of labor
 * costed wrong on every document downstream.
 *
 * The model unit is whatever the field behind it stores — minutes in the
 * pricebook's labor lines, hours on a document line — and is converted here so
 * no caller has to divide by 60 on the way past.
 */
interface DurationFieldProps {
  /** Wrapper class. Use `inputClassName` to size the box itself. */
  className?: string;
  inputClassName?: string;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Inline table rows and pricing strips have no room for the clock or a line
   * of error text below the box; there the invalid state is carried by
   * `aria-invalid` and the ring alone.
   */
  compact?: boolean;
}

interface DurationMinutesInputProps extends DurationFieldProps {
  /** Minutes. */
  value: number;
  onChange: (minutes: number) => void;
}

interface DurationHoursInputProps extends DurationFieldProps {
  /** Hours, the unit `estimatedHours` and the T&M line fields store. */
  value: number | null | undefined;
  onChange: (hours: number) => void;
}

/** Hours held to the same precision `parseDurationToHours` commits. */
function hoursToMinutes(hours: number | null | undefined): number {
  return Math.round((hours || 0) * 60);
}

function DurationField({
  minutes,
  onChangeMinutes,
  className,
  inputClassName,
  id,
  'aria-label': ariaLabel = 'Duration',
  'aria-describedby': ariaDescribedBy,
  placeholder = DURATION_PLACEHOLDER,
  disabled,
  compact = false,
}: DurationFieldProps & { minutes: number; onChangeMinutes: (minutes: number) => void }) {
  const [draft, setDraft] = useState(() => formatDurationFromMinutes(minutes));
  const [invalid, setInvalid] = useState(false);
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setDraft(formatDurationFromMinutes(minutes));
  }, [minutes]);

  function commit() {
    const parsed = parseDurationToMinutes(draft);
    if (parsed == null) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    setDraft(formatDurationFromMinutes(parsed));
    onChangeMinutes(parsed);
  }

  return (
    <div className={className}>
      <div className="relative">
        {compact ? null : (
          <Clock3
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
        )}
        <Input
          id={id}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          aria-invalid={invalid}
          disabled={disabled}
          value={draft}
          placeholder={placeholder}
          className={cn('tabular-nums', compact ? undefined : 'pl-9', inputClassName)}
          onChange={(event) => {
            const nextDraft = event.target.value;
            setDraft(nextDraft);
            if (invalid) setInvalid(false);
            // Keep the form model current while the user types, so figures
            // derived from labor time — a line subtotal, the document total —
            // track the box instead of lagging a blur behind. Formatting is
            // deferred until blur so "1h 30m" is not rewritten mid-word.
            const parsed = parseDurationToMinutes(nextDraft);
            if (parsed != null) onChangeMinutes(parsed);
          }}
          onFocus={() => {
            focused.current = true;
          }}
          onBlur={() => {
            focused.current = false;
            commit();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
              event.currentTarget.blur();
            }
          }}
        />
      </div>
      {invalid && !compact ? (
        <p className="mt-1 text-xs text-destructive">{DURATION_HINT}</p>
      ) : null}
    </div>
  );
}

/** For state stored as minutes — the pricebook's labor lines and rate tiers. */
export function DurationMinutesInput({ value, onChange, ...props }: DurationMinutesInputProps) {
  return <DurationField {...props} minutes={value} onChangeMinutes={onChange} />;
}

/** For state stored as hours — `estimatedHours`, `actualHours`, T&M line hours. */
export function DurationHoursInput({ value, onChange, ...props }: DurationHoursInputProps) {
  return (
    <DurationField
      {...props}
      minutes={hoursToMinutes(value)}
      onChangeMinutes={(minutes) => onChange(Math.round((minutes / 60) * 1e6) / 1e6)}
    />
  );
}
